/**
 * Explicit-Euler ODE integrator for the calculus demo.
 *
 * Given a slope function `g(x, y) = dy/dx`, integrates the ODE forward (or
 * backward with a negative step `h`) from `start` using the standard scheme:
 *
 *   y_{n+1} = y_n + h · g(x_n, y_n)
 *   x_{n+1} = x_n + h
 *
 * The start point is always included. Integration stops early if a computed
 * value is non-finite (asymptote or blow-up).
 */

import type { Vec2 } from '@learnforge/demo-math/geometry';

/**
 * Integrate `dy/dx = g(x, y)` using the explicit Euler method.
 *
 * @param g      The slope function `dy/dx = g(x, y)`.
 * @param start  Initial point `{ x, y }`.
 * @param h      Step size. Negative `h` integrates backward.
 * @param steps  Number of steps to take (result has at most `steps + 1` points).
 * @returns      Array of `Vec2` points along the solution curve, starting at
 *               `start`. Truncated early if a non-finite value is produced.
 */
export function eulerCurve(
  g: (x: number, y: number) => number,
  start: Vec2,
  h: number,
  steps: number,
): Vec2[] {
  const pts: Vec2[] = [{ x: start.x, y: start.y }];
  let x = start.x;
  let y = start.y;

  for (let i = 0; i < steps; i++) {
    const slope = g(x, y);
    const nextY = y + h * slope;
    const nextX = x + h;

    if (!Number.isFinite(nextY) || !Number.isFinite(nextX)) {
      break;
    }

    x = nextX;
    y = nextY;
    pts.push({ x, y });
  }

  return pts;
}
